import { Card as MuiCard, CardContent, CardHeader, CardActions, Box, useMediaQuery, useTheme } from '@mui/material'
import { Line } from 'react-chartjs-2'
import '@utils/chartSetup'

const Card = ({
  children,
  title,
  subtitle,
  action,
  footer,
  padding = 3,
  className = '',
  ...props
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const responsivePadding = isMobile ? Math.max(padding - 1, 2) : padding

  return (
    <MuiCard className={className} {...props}>
      {(title || action) && (
        <CardHeader
          title={title}
          subheader={subtitle}
          action={action}
          sx={{
            pb: title && !subtitle ? 1 : 2,
            px: { xs: 2, sm: 3 },
            '& .MuiCardHeader-title': {
              fontSize: { xs: '1rem', sm: '1.25rem' },
            },
          }}
        />
      )}
      <CardContent sx={{ pt: title ? 0 : responsivePadding, px: { xs: 2, sm: padding }, pb: { xs: 2, sm: padding } }}>
        {children}
      </CardContent>
      {footer && (
        <CardActions sx={{ px: { xs: 2, sm: padding }, pb: { xs: 2, sm: padding } }}>
          {footer}
        </CardActions>
      )}
    </MuiCard>
  )
}

// Tiny decorative trend line for a StatCard. Carries no standalone meaning
// (the value + trend text do), so it's aria-hidden.
const Sparkline = ({ data, color }) => {
  const theme = useTheme()
  const stroke = theme.palette[color]?.main || theme.palette.primary.main
  const chartData = {
    labels: data.map((_, i) => i),
    datasets: [
      {
        data,
        borderColor: stroke,
        backgroundColor: `${stroke}22`,
        borderWidth: 1.5,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
      },
    ],
  }
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    scales: { x: { display: false }, y: { display: false, min: 0 } },
  }
  return (
    <Box aria-hidden="true" sx={{ height: 34, mt: 1 }}>
      <Line data={chartData} options={options} />
    </Box>
  )
}

export const StatCard = ({ title, value, icon, trend, sparkline, subtitle, color = 'primary', onClick }) => {
  return (
    <MuiCard
      sx={{
        // Fill the grid cell so cards in the same row match the tallest one
        // (e.g. the KPI cards that carry a sparkline don't dwarf the plain ones).
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: 3 } : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1} minWidth={0}>
            <Box
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              {title}
            </Box>
            <Box
              sx={{
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              {value}
            </Box>
            {subtitle && (
              <Box
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  mt: 0.5,
                }}
              >
                {subtitle}
              </Box>
            )}
            {trend && (
              <Box
                sx={{
                  fontSize: '0.75rem',
                  color: trend.color || (trend.positive ? 'success.main' : 'error.main'),
                  mt: 0.5,
                }}
              >
                {trend.text != null
                  ? trend.text
                  : `${trend.positive ? '+' : ''}${trend.value}% ${trend.label}`}
              </Box>
            )}
            {sparkline && sparkline.length > 0 && (
              <Sparkline data={sparkline} color={color} />
            )}
          </Box>
          {icon && (
            <Box
              sx={{
                backgroundColor: (theme) => {
                  const colorMap = {
                    primary: 'rgba(27, 94, 122, 0.12)',
                    info: 'rgba(33, 150, 243, 0.12)',
                    warning: 'rgba(255, 152, 0, 0.12)',
                    error: 'rgba(244, 67, 54, 0.12)',
                    success: 'rgba(76, 175, 80, 0.12)',
                  }
                  return colorMap[color] || 'rgba(27, 94, 122, 0.12)'
                },
                color: `${color}.main`,
                p: { xs: 1, sm: 1.5 },
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                ml: 1,
                '& .MuiSvgIcon-root': {
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                },
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
      </CardContent>
    </MuiCard>
  )
}

export default Card
